import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PalmettoSideMenu from '@zawarski/palmetto-react-side-menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/SettingsApplications';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import ControlPanelIcon from '@mui/icons-material/BallotRounded';
import IncidentIcon from '@mui/icons-material/ArticleRounded';
import PositionIcon from '@mui/icons-material/Assignment';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { useAppSelector } from 'store/hooks';
import { accountSelector, activeGroupNameSelector } from 'store/account/account.selector';
import { userAdminSelector } from 'store/auth/auth.selector';
import type {
  MenuList,
  MenuItems,
} from '@zawarski/palmetto-react-side-menu/dist/cjs/types/interfaces/MenuItems';

const appTitle = import.meta.env.VITE_APP_TITLE ?? 'Welcome to Dashboard';

const DrawerMenu: React.FC = () => {
  const navigate = useNavigate();
  const groupName = useAppSelector(activeGroupNameSelector) || '';
  const account = useAppSelector(accountSelector);
  const UserFullName =
    account !== null ? `${account?.pvPersonGivenName} ${account?.pvPersonSurName}` : '';
  const isUserAdmin = useAppSelector(userAdminSelector);

  const defaultMenu: MenuItems = useMemo(() => {
    return {
      menu: [
        {
          key: 'incidents',
          name: 'Incident',
          path: '/incidents',
          startIcon: <IncidentIcon />,
        },
        {
          key: 'onboarding',
          name: 'Onboarding',
          path: '/onboarding',
          startIcon: <MenuBookIcon />,
        },
        {
          key: 'settings',
          name: 'Settings',
          path: '/settings',
          startIcon: <SettingsApplicationsIcon />,
        },
      ],
      section: [
        {
          key: 'contact',
          name: 'Contact Support',
          path: '/contact-support',
          endIcon: <LiveHelpIcon />,
        },
        {
          key: 'logout',
          name: 'Log out',
          path: '/logout',
          endIcon: <LogoutIcon sx={{ color: '#eb021d' }} />,
        },
      ],
      version: {
        name: 'Version 1.0.0', // Define the version number
        color: '#999', // Define the default color
      },
    };
  }, [groupName, UserFullName]);

  const adminMenu: MenuItems = useMemo(() => {
    return {
      menu: [
        {
          key: 'admin',
          name: 'Admin',
          path: 'admin',
          startIcon: <SettingsIcon />,
          subMenu: [
            {
              key: 'control-panel',
              name: 'Control Panel',
              path: '/control-panel',
              startIcon: <ControlPanelIcon />,
            },
            {
              key: 'incidents',
              name: 'Incident',
              path: '/incidents',
              startIcon: <IncidentIcon />,
            },
            {
              key: 'positions',
              name: 'Positions',
              path: '/positions',
              startIcon: <PositionIcon />,
            },
          ],
        },
        {
          key: 'onboarding',
          name: 'Onboarding',
          path: '/onboarding',
          startIcon: <MenuBookIcon />,
        },
        {
          key: 'settings',
          name: 'Settings',
          path: '/settings',
          startIcon: <SettingsApplicationsIcon />,
        },
      ],
      section: [
        {
          key: 'contact',
          name: 'Contact Support',
          path: '/contact-support',
          endIcon: <LiveHelpIcon />,
        },
        {
          key: 'logout',
          name: 'Log out',
          path: '/logout',
          endIcon: <LogoutIcon sx={{ color: '#eb021d' }} />,
        },
      ],
      version: {
        name: 'Version 1.0.0', // Define the version number
        color: '#999', // Define the default color
      },
    };
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [activePath, setActivePath] = useState<string>('/dashboard');
  const [activeSubMenu, setActiveSubMenu] = useState(false);

  useEffect(() => {
    const handleToggle = () => setIsOpen((prevState) => !prevState);
    window.addEventListener('toggle-sidebar', handleToggle); // Listen for the event
    return () => window.removeEventListener('toggle-sidebar', handleToggle);
  }, []);

  function findValueByKey(arr: MenuList, pathToFind: string) {
    for (const obj of arr) {
      if (obj.path === pathToFind) {
        return obj.name; // *: To select the name of the subMenu
      }

      if (obj.subMenu && obj.subMenu.length) {
        const nestedResult = findValueByKey(obj.subMenu, pathToFind);
        if (nestedResult !== undefined) {
          return nestedResult;
        }
      }
    }
    return undefined;
  }

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = '/';
  };

  const handleMenuClick = (link: string) => {
    setActivePath(link);
    const menuLists = isUserAdmin ? adminMenu : defaultMenu;
    const menu = menuLists.menu.find((item) => item.path === link);
    if (activeSubMenu) {
      const subMenu = findValueByKey(menuLists.menu, link);
      if (subMenu && !menu) {
        if (link === '/incidents') {
          window.dispatchEvent(new CustomEvent('open-incident'));
        }
        if (link === '/positions') {
          window.dispatchEvent(new CustomEvent('open-positions'));
        }
        if (link === '/control-panel') {
          window.dispatchEvent(new CustomEvent('open-control-panel'));
        }
        setActiveSubMenu((prevState) => !prevState);
        setIsOpen(false);
      }
    }
    if (menu) {
      if (menu.subMenu && menu.subMenu.length) {
        // For Submenu Action
        setActiveSubMenu((prevState) => !prevState);
      } else {
        setIsOpen(false);
      }
    } else {
      const section = menuLists.section.find((item) => item.path === link);
      if (section) {
        setIsOpen(false);
      }
    }
    if (!link.includes('/')) return null;
    if (link === '/logout') return handleLogout();
    if (link === '/settings') {
      navigate(link);
      setIsOpen(false);
    }
    if (link === '/onboarding') {
      setIsOpen(false);
      return window.dispatchEvent(new CustomEvent('open-onboarding'));
    }
    if (link === '/contact-support') {
      setIsOpen(false);
      return window.dispatchEvent(new CustomEvent('open-contact-support'));
    }
    if (link === '/incidents') {
      window.dispatchEvent(new CustomEvent('open-incident'));
    }
  };

  const handleDeactivateSubMenu = () => setActiveSubMenu(false);

  return (
    <PalmettoSideMenu
      srcLogo={'/images/launcher.png'}
      menuItems={isUserAdmin ? adminMenu : defaultMenu}
      isDrawerOpen={isOpen}
      activePath={activePath}
      activeSubMenu={activeSubMenu}
      programTitle={appTitle}
      onDrawerClose={() => setIsOpen(false)}
      onMenuClick={handleMenuClick}
      deactivateSubMenu={handleDeactivateSubMenu}
    />
  );
};

export default DrawerMenu;